import { useState, useEffect, useCallback } from 'react';
import { useTestContext } from '../../../context/testContext';
import { useCalibration } from '../../../context/calibrationContext';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { getLogmarLineSize500mm } from '../../../helpers/functions';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { useLogmarData, useContrastSensitivityData } from '../../../helpers/reducers';
import ContrastSensitivityInfo from '../../../components/ContrastSensitivity/ContrastSensitivityInfo';
import ContrastSensitivityControls from '../../../components/ContrastSensitivity/ContrastSensitivityControls';
import AutoResizeControls from '../exams/AutoResizeControls';
import { SessionActions, useSessionUI } from '../../../ui';
import { StepBody, Divider, useConfirmLeave } from '../testScreenKit';

// Step 3 - scoring (Figma session/14 left, landscape 23 right).
const ContrastSensitivityTest = ({ eyeTested, updateStep }) => {
  const { confirm } = useSessionUI();
  const confirmLeave = useConfirmLeave();
  const {
    calibrationData: { pixelDensity, distanceFromScreen, methodSelected },
  } = useCalibration();
  const { testData, updateTestData, contrastSensitivityData } = useTestContext();

  const postMessage = usePostMessage();
  const { pixelDensityReference, patientDistanceReference } = calibrationConstants;
  const [initialLoad, setInitialLoad] = useState(false);
  const messageFromPatient = useReceiveMessage();
  const {
    updateContrastSensitivityData,
    currentContrastSensitivityData: { contrastValue, fontColor },
  } = useContrastSensitivityData();
  const {
    updateLogmarData,
    currentLogmarData: { logmarSize },
  } = useLogmarData();

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  useEffect(() => {
    updateLogmarData({ type: 'setInitialLogmarSize', payload: testData[eyeTested] });
  }, [updateLogmarData, eyeTested]);

  useEffect(() => {
    if (messageFromPatient.type === 'patientMovementDetected') {
      if (messageFromPatient.content.patientIsMoving === false) {
        updateLogmarFontSize(logmarSize, messageFromPatient.content.patientDistance);
      }
    }
  }, [messageFromPatient]);

  const getLogmarFontSize = (logmar, distance) => {
    let lineSize;
    if (patientDistanceReference === 500) {
      lineSize = getLogmarLineSize500mm(logmar.id);
    }
    const fontSize = (lineSize * distance) / patientDistanceReference / pixelDensity;
    const fontSizeAdjustment = (pixelDensity * testData.fontAdjustmentPercentage) / pixelDensityReference / 100;
    const adjustedFontSize = fontSize + fontSize * fontSizeAdjustment;
    return adjustedFontSize;
  };

  const updateLogmarFontSize = (logmar, distance) => {
    const fontSize = Math.round(getLogmarFontSize(logmar, distance));
    postMessage({
      type: 'resizeLogmar',
      content: {
        fontSize: fontSize,
        distanceReferenceInMM: distance,
        logmarCharacters: contrastSensitivityData['settings']['characters'],
        fontColor: fontColor,
      },
    });
  };

  useEffect(() => {
    updateLogmarFontSize(logmarSize, distanceFromScreen);
  }, [logmarSize]);

  const clearExistingResults = useCallback(() => {
    const existingResults = [...(testData?.contrastSensitivityResults || [])];
    const resultsWithoutCurrentEye = existingResults.filter((result) => {
      return !result.hasOwnProperty(eyeTested);
    });

    const shouldClearAllResults =
      (testData.testLeftEye && !testData.testRightEye) || (testData.testRightEye && !testData.testLeftEye) || testData.testBothEyes;

    updateTestData({ contrastSensitivityResults: shouldClearAllResults ? [] : resultsWithoutCurrentEye });
  }, [eyeTested, testData.testLeftEye, testData.testRightEye, testData.testBothEyes, updateTestData]);

  useEffect(() => {
    if (initialLoad === false) {
      updateContrastSensitivityData({ type: 'setFontColor', payload: contrastSensitivityData['settings']['fontColor'] });

      postMessage({
        type: 'contrastSensitivity',
        content: {
          fontSize: Math.round(getLogmarFontSize(logmarSize, distanceFromScreen)),
          distanceFromScreen: distanceFromScreen,
          methodSelected: methodSelected,
          characters: contrastSensitivityData['settings']['characters'],
          fontColor: `hsl(0, 0%, 0%)`,
          testMode: methodSelected !== 'manual' ? 'auto' : testData.testMode,
        },
      });

      clearExistingResults();
      setInitialLoad(true);
    }
  }, [initialLoad]);

  const endTest = async () => {
    const results = testData.contrastSensitivityResults || [];
    const nextStep = eyeTested === 'rightEye' && testData.testLeftEye ? 'leftEyeSetup' : 'results';

    if (!results.some((result) => result[eyeTested])) {
      if (await confirm({ message: 'No results have been saved for this test. Are you sure you want to continue?' })) {
        return updateStep(nextStep);
      }
    } else return updateStep(nextStep);
  };

  const goBack = () =>
    confirmLeave().then(() => {
      updateTestData({ [eyeTested]: null, testInProgress: false });
      updateStep(`${eyeTested}Setup`);
    });

  return (
    <StepBody gap={18} data-testid='cs-scoring'>
      <ContrastSensitivityControls contrastValue={contrastValue} fontColor={fontColor} updateContrastSensitivityData={updateContrastSensitivityData} />
      <ContrastSensitivityInfo logmarSize={logmarSize} contrastValue={contrastValue} updateLogmarData={updateLogmarData} eyeTested={eyeTested} />
      <Divider />
      <AutoResizeControls inline />
      <SessionActions onBack={goBack} primary={{ label: eyeTested === 'rightEye' && testData.testLeftEye ? 'Next Eye' : 'Next', onClick: () => endTest() }} />
    </StepBody>
  );
};

export default ContrastSensitivityTest;
